// Preseason scoutingová databáze — veřejný dashboard z redakčních dat.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NotePencil, TrendUp, TrendDown, ArrowsClockwise } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

export const trendBucket = (t) => {
    const s = (t || "").trim().toLowerCase();
    if (s === "↑↑" || s.includes("výrazně roste")) return 2;
    if (s.startsWith("↑") || s.includes("roste")) return 1;
    if (s === "↓↓" || s.includes("výrazně klesá")) return -2;
    if (s.startsWith("↓") || s.includes("klesá")) return -1;
    return 0;
};

export const TREND_CLS = (t) => {
    const b = trendBucket(t);
    if (b === 2) return "text-fa-mint font-bold";
    if (b === 1) return "text-fa-mint";
    if (b === -2) return "text-red-300 font-bold";
    if (b === -1) return "text-red-300";
    return "text-white/50";
};

export function MatchCard({ m }) {
    return (
        <div className="border border-white/15 bg-[#0d0d0d] p-3 space-y-1.5" data-testid={`psdb-match-${m.unique_key}`}>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[9px] text-white/40 tabular-nums">{m.date}</span>
                <Link to={`/preseason/club/${m.club_key}`} className="font-display font-bold text-sm hover:text-fa-sky">{m.club}</Link>
                <span className="font-mono text-[10px] text-white/50">vs {m.opponent}</span>
                {m.result && <span className="font-mono text-[11px] font-bold text-white/85">{m.result}</span>}
                {m.formation && <span className="font-mono text-[9px] px-1.5 py-0.5 border border-white/15 text-white/55">{m.formation}</span>}
                {m.relevance != null && (
                    <span className={`font-mono text-[9px] px-1.5 py-0.5 border ml-auto ${m.relevance >= 7 ? "border-fa-mint/60 text-fa-mint" : "border-white/20 text-white/50"}`}>
                        relevance {m.relevance}/10
                    </span>
                )}
            </div>
            {m.fpl_winners && <p className="font-mono text-[10px] text-fa-mint/90">▲ {m.fpl_winners}</p>}
            {m.fpl_risks && <p className="font-mono text-[10px] text-red-300/90">▼ {m.fpl_risks}</p>}
            {m.tactical_notes && <p className="font-mono text-[10px] text-white/60">{m.tactical_notes}</p>}
        </div>
    );
}

export default function PreseasonDatabase() {
    const [data, setData] = useState(null);
    const [club, setClub] = useState("");
    const [trend, setTrend] = useState("");

    useEffect(() => {
        track("tool_open", { tool: "preseason_database" });
        api.get("/preseason-db/overview", { timeout: 30000 }).then((r) => setData(r.data)).catch(() => {});
    }, []);

    const perfs = useMemo(() => (data?.performances || []).filter((p) =>
        (!club || p.club_key === club) &&
        (!trend ||
            (trend === "up" && trendBucket(p.trend) > 0) ||
            (trend === "down" && trendBucket(p.trend) < 0) ||
            (trend === "flat" && trendBucket(p.trend) === 0))), [data, club, trend]);

    if (!data) {
        return <AppShell><div className="border border-white/10 bg-[#0d0d0d] p-8 font-mono text-[11px] text-white/50 flex items-center gap-2"><ArrowsClockwise size={13} className="animate-spin" /> Načítám databázi…</div></AppShell>;
    }

    return (
        <AppShell>
            <div className="space-y-5" data-testid="psdb-page">
                <header className="border-2 border-fa-sky/40 bg-gradient-to-br from-fa-sky/10 via-transparent to-transparent p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-sky flex items-center gap-2">
                        <NotePencil size={11} weight="fill" /> Redakční scouting
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Preseason databáze</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Ručně scoutované přáteláky 26/27 — sestavy, minutáž, taktické poznatky, FPL vítězové a rizika.
                        {data.last_sync && <span className="text-white/40"> Aktualizováno {String(data.last_sync).slice(0, 16).replace("T", " ")} UTC.</span>}
                    </p>
                    <div className="flex gap-4 mt-3 font-mono text-[10px] text-white/55 flex-wrap">
                        <span><b className="text-fa-sky">{data.matches.length}</b> zápasů</span>
                        <span><b className="text-fa-sky">{data.clubs.length}</b> klubů</span>
                        <span><b className="text-fa-sky">{data.performances.length}</b> hráčských výkonů</span>
                        <Link to="/preseason/vs-reality" data-testid="psdb-vsr-link" className="text-fa-mint hover:underline">→ Očekávání vs. realita</Link>
                    </div>
                </header>

                {(data.rising.length > 0 || data.falling.length > 0) && (
                    <div className="grid md:grid-cols-2 gap-4">
                        <section className="border border-fa-mint/30 bg-fa-mint/5 p-3" data-testid="psdb-rising">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint flex items-center gap-1.5 mb-1.5"><TrendUp size={11} /> Rising</div>
                            <div className="flex flex-wrap gap-1.5">
                                {data.rising.map((p, i) => (
                                    <span key={i} className="font-mono text-[10px] px-2 py-1 border border-fa-mint/30">
                                        <b>{p.player_name}</b> <span className="text-white/45">{p.club}</span>
                                    </span>
                                ))}
                            </div>
                        </section>
                        <section className="border border-red-400/25 bg-red-400/5 p-3" data-testid="psdb-falling">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-red-300 flex items-center gap-1.5 mb-1.5"><TrendDown size={11} /> Falling</div>
                            <div className="flex flex-wrap gap-1.5">
                                {data.falling.map((p, i) => (
                                    <span key={i} className="font-mono text-[10px] px-2 py-1 border border-red-400/25">
                                        <b>{p.player_name}</b> <span className="text-white/45">{p.club}</span>
                                    </span>
                                ))}
                            </div>
                        </section>
                    </div>
                )}

                <section className="space-y-2" data-testid="psdb-matches">
                    <h2 className="font-display font-black uppercase text-lg">Poslední přáteláky</h2>
                    <div className="grid lg:grid-cols-2 gap-2">
                        {data.matches.slice(0, 12).map((m) => <MatchCard key={m.unique_key} m={m} />)}
                    </div>
                </section>

                <section className="space-y-2" data-testid="psdb-performances">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display font-black uppercase text-lg">Hráčské výkony</h2>
                        <select value={club} onChange={(e) => setClub(e.target.value)} data-testid="psdb-filter-club"
                                className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                            <option value="">Všechny kluby</option>
                            {data.clubs.map((c) => <option key={c.club_key} value={c.club_key}>{c.name}</option>)}
                        </select>
                        <select value={trend} onChange={(e) => setTrend(e.target.value)} data-testid="psdb-filter-trend"
                                className="bg-black/40 border border-white/25 px-2 py-1.5 font-mono text-[10px] text-white focus:outline-none">
                            <option value="">Všechny trendy</option>
                            <option value="up">Roste</option>
                            <option value="down">Klesá</option>
                            <option value="flat">Neutrální</option>
                        </select>
                    </div>
                    <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="font-mono text-[8px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                    <th className="px-3 py-2">Hráč</th><th className="px-3 py-2">Klub</th><th className="px-3 py-2">Datum</th>
                                    <th className="px-3 py-2 text-right">Min</th><th className="px-3 py-2 text-center">Start</th>
                                    <th className="px-3 py-2 text-right">G+A</th><th className="px-3 py-2">Trend</th><th className="px-3 py-2">Poznámka</th>
                                </tr>
                            </thead>
                            <tbody>
                                {perfs.map((p) => (
                                    <tr key={p.unique_key} className="border-b border-white/5 hover:bg-white/[0.02] font-mono text-[10px]" data-testid={`psdb-perf-${p.unique_key}`}>
                                        <td className="px-3 py-1.5 font-display font-bold text-[12px]">{p.player_name}</td>
                                        <td className="px-3 py-1.5 text-white/55"><Link to={`/preseason/club/${p.club_key}`} className="hover:text-fa-sky">{p.club}</Link></td>
                                        <td className="px-3 py-1.5 text-white/45 tabular-nums">{p.date}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-white/80">{p.minutes ?? "Neověřeno"}</td>
                                        <td className="px-3 py-1.5 text-center">{p.started === true ? "✓" : p.started === false ? "—" : "?"}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{(p.goals ?? 0) + (p.assists ?? 0) || ""}</td>
                                        <td className={`px-3 py-1.5 ${TREND_CLS(p.trend)}`}>{p.trend || "Neověřeno"}</td>
                                        <td className="px-3 py-1.5 text-white/55 max-w-xs">{p.notes}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </AppShell>
    );
}
